import React, { useEffect, useMemo, useRef, useState } from 'react'
import Editor from '@monaco-editor/react'

import {
  ESPER_LANGUAGE_ID,
  ensureEsperLanguage,
  initializeMonaco,
  getSnippetCompletions
} from '../app/monacoSetup.js'
import {
  buildCompletionCatalog,
  createEsperSupportCompletionItems,
  createFieldCompletionItems
} from '../utils/schemaCompletion.js'

const VALIDATION_MARKER_OWNER = 'esper-validation'

const StatementEditor = ({
  value,
  onChange,
  onBlur,
  validationMarkers = [],
  readOnly = false,
  height = '60vh',
  fieldSchema,
  eventSamples,
  ariaLabel = 'Esper statement editor'
}) => {
  const editorRef = useRef(null)
  const monacoRef = useRef(null)
  const completionDisposableRef = useRef(null)
  const [completionEntries, setCompletionEntries] = useState([])

  // Pre-load Monaco language configuration on first mount to avoid blank editor flashes.
  useMemo(() => {
    initializeMonaco().catch((error) => {
      console.error('[StatementEditor] Failed to initialise Monaco', error)
    })
  }, [])

  useEffect(() => {
    return () => {
      completionDisposableRef.current?.dispose()
    }
  }, [])

  useEffect(() => {
    if (!editorRef.current || !monacoRef.current) {
      return
    }

    const model = editorRef.current.getModel()
    if (!model) {
      return
    }

    monacoRef.current.editor.setModelMarkers(model, VALIDATION_MARKER_OWNER, validationMarkers)
  }, [validationMarkers])

  useEffect(() => {
    const timeout = setTimeout(() => {
      const catalog = buildCompletionCatalog({ fieldSchema, events: eventSamples })
      setCompletionEntries(catalog.merged)
    }, 200)

    return () => clearTimeout(timeout)
  }, [fieldSchema, eventSamples])

  const registerCompletionProvider = (monaco) => {
    completionDisposableRef.current?.dispose()

    completionDisposableRef.current = monaco.languages.registerCompletionItemProvider(ESPER_LANGUAGE_ID, {
      triggerCharacters: ['.', ':', ' ', '[' ],
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position)
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn
        }

        // Schema-derived field completions
        const fieldSuggestions = createFieldCompletionItems(completionEntries, monaco, range)

        // Esper language completions (keywords, functions, operators)
        const esperSuggestions = createEsperSupportCompletionItems(monaco, range)

        // EPL snippet templates
        const snippets = getSnippetCompletions()
        const snippetSuggestions = snippets.map((snippet) => ({
          label: snippet.label,
          insertText: snippet.insertText,
          documentation: snippet.documentation,
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          sortText: `0_${snippet.label}`, // Prioritize snippets
          range
        }))

        // Merge all completions: snippets first, then fields, then language elements
        return {
          suggestions: [...snippetSuggestions, ...fieldSuggestions, ...esperSuggestions]
        }
      }
    })
  }

  const handleBeforeMount = (monaco) => {
    ensureEsperLanguage(monaco)
  }

  const handleEditorMount = (editor, monaco) => {
    if (!editor) {
      console.error('[StatementEditor] Editor mount failed: editor is undefined')
      return
    }

    if (!monaco) {
      console.error('[StatementEditor] Editor mount failed: monaco is undefined')
      return
    }

    console.log('[StatementEditor] Editor mounted successfully')
    editorRef.current = editor
    monacoRef.current = monaco

    registerCompletionProvider(monaco)

    editor.onDidBlurEditorWidget(() => {
      onBlur?.(editor.getValue())
    })
  }

  useEffect(() => {
    if (!monacoRef.current) {
      return
    }
    registerCompletionProvider(monacoRef.current)
  }, [completionEntries])

  return (
    <div className="statement-editor" data-testid="statement-editor">
      <Editor
        key="monaco-editor-instance"
        value={value}
        language={ESPER_LANGUAGE_ID}
        theme="esper-dark"
        options={{
          minimap: { enabled: false },
          wordWrap: 'on',
          fontSize: 14,
          automaticLayout: true,
          readOnly,
          smoothScrolling: true,
          scrollBeyondLastLine: false,
          contextmenu: true
        }}
        beforeMount={handleBeforeMount}
        onMount={handleEditorMount}
        onChange={(nextValue) => onChange?.(nextValue ?? '')}
        height={height}
        aria-label={ariaLabel}
        keepCurrentModel={true}
      />
    </div>
  )
}

export default StatementEditor
